import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { ApiError, createApiClient } from '../../services/api/client'
import { fetchAuthenticatedAudio } from '../../services/api/authenticated-audio'
import {
  callQualityAnalysisSchema,
  callTranscriptSchema,
  interactionCallPageSchema,
  type CallQualityAnalysis,
  type CallTranscript,
  type InteractionCallPage,
} from './interaction-calls-contracts'

export type InteractionCallsRepository = {
  getCalls: (
    tenantId: string,
    aggregateId: string,
    cursor?: string,
    signal?: AbortSignal
  ) => Promise<InteractionCallPage>
  getRecording: (
    tenantId: string,
    aggregateId: string,
    callRef: string,
    signal?: AbortSignal
  ) => Promise<Blob>
  getTranscript: (
    tenantId: string,
    aggregateId: string,
    callRef: string,
    signal?: AbortSignal
  ) => Promise<CallTranscript>
  getQualityAnalysis: (
    tenantId: string,
    aggregateId: string,
    callRef: string,
    signal?: AbortSignal
  ) => Promise<CallQualityAnalysis>
}

function callPath(tenantId: string, aggregateId: string, suffix = '') {
  const base = `/dashboard/clinics/${encodeURIComponent(tenantId)}/interaction-aggregates/${encodeURIComponent(aggregateId)}/calls`
  return suffix ? `${base}/${suffix}` : base
}

export function createInteractionCallsRepository({
  getUser,
  baseUrl = getEnvironment().userManagementBaseUrl,
  fetchImplementation = fetch,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): InteractionCallsRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })

  async function getRecording(
    tenantId: string,
    aggregateId: string,
    callRef: string,
    signal?: AbortSignal
  ) {
    return fetchAuthenticatedAudio({
      baseUrl,
      path: callPath(
        tenantId,
        aggregateId,
        `${encodeURIComponent(callRef)}/recording`
      ),
      getUser,
      fetchImplementation,
      signal,
    })
  }

  return {
    async getCalls(tenantId, aggregateId, cursor, signal) {
      const search = new URLSearchParams({
        pageSize: '10',
        contextVersion: '2',
        interactionFacts: '1',
      })
      if (cursor) search.set('cursor', cursor)
      const page = await client.request(
        `${callPath(tenantId, aggregateId)}?${search.toString()}`,
        {
          method: 'GET',
          signal,
          responseSchema: interactionCallPageSchema,
        }
      )
      if (
        page.aggregateId !== aggregateId ||
        page.calls.some((call) => call.aggregateId !== aggregateId)
      ) {
        throw new ApiError(
          'invalid-response',
          'The dashboard service returned an invalid response'
        )
      }
      return page
    },
    getRecording,
    async getTranscript(tenantId, aggregateId, callRef, signal) {
      const transcript = await client.request(
        callPath(
          tenantId,
          aggregateId,
          `${encodeURIComponent(callRef)}/transcript?interactionFacts=1`
        ),
        { method: 'GET', signal, responseSchema: callTranscriptSchema }
      )
      if (transcript.callRef !== callRef) {
        throw new ApiError(
          'invalid-response',
          'The dashboard service returned an invalid response'
        )
      }
      return transcript
    },
    async getQualityAnalysis(tenantId, aggregateId, callRef, signal) {
      const quality = await client.request(
        callPath(
          tenantId,
          aggregateId,
          `${encodeURIComponent(callRef)}/quality-analysis`
        ),
        { method: 'GET', signal, responseSchema: callQualityAnalysisSchema }
      )
      if (quality.callRef !== callRef) {
        throw new ApiError(
          'invalid-response',
          'The dashboard service returned an invalid response'
        )
      }
      return quality
    },
  }
}
